/**
 * Connect a device — the PC-side gateway's device onboarding flow:
 * Discover -> Registers -> Map -> Assign -> Go-live.
 *
 * Owns the wizard state and routes between the five step panels
 * (components/connect/*). The only cloud WRITE happens once at Go-live
 * (`commitConnection`): create the discovered device as a child instrument Thing
 * on the chosen gateway (which config-pushes it), then bind its tags to the
 * equipment's ports. Discovery and the register sniff are read-only scans the
 * chosen gateway runs on its plant network, relayed through the cloud
 * (lib/gateway-client), so every device and value shown came from a real
 * gateway. Without the live API the scan steps say so instead of inventing
 * devices; a manually keyed device still reaches the Map step.
 */

import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { PageContainer } from "@/routes/_layout";
import { PageHeader } from "@/lib/ui/page-header";
import { Button } from "@/lib/ui";
import { ConfirmDialog } from "@/lib/ui/confirm-dialog";
import { WizardLayout } from "@/lib/ui/wizard-layout";
import { useToast } from "@/lib/ui/toast";
import { useOrg } from "@/lib/org-context";
import { useQueryClient } from "@tanstack/react-query";
import {
  isLiveApi,
  toPlantNode,
  useThingsQuery,
  useTwinEntitiesQuery,
  TWIN_ENTITIES_KEY,
} from "@/lib/api";
import { blankRow, type TagRow } from "@/components/iot/ThingTagRows";
import { type DiscoveredDevice, type SniffedRegister } from "@/lib/gateway-client";
import {
  CONNECT_POLL_DEFAULT,
  commitConnection,
  goLiveErrorMessage,
  goLiveRowErrors,
  goLiveToast,
  pointToTagRow,
  registerMapToRows,
  registerToTagRow,
  slugish,
  type CommitResult,
  type RefusedPoint,
  type RegisterMap,
} from "@/components/connect/commit";
import { CONNECT_STEPS, CONNECT_STEP_ORDER, type ConnectStepId } from "@/components/connect/steps";
import { ConnectStepper } from "@/components/connect/ConnectStepper";
import { ConnectSummaryBar } from "@/components/connect/ConnectSummaryBar";
import { canJumpTo, connectSummary, enterAdvances } from "@/components/connect/summary";
import { DeviceScanPanel } from "@/components/connect/DeviceScanPanel";
import { ProtocolStep } from "@/components/connect/ProtocolStep";
import { ConnectionForm } from "@/components/connect/ConnectionForm";
import { useGatewayCards, useGatewayDrivers, useGatewayThing } from "@/components/connect/gateway-queries";
import { preferredGateway } from "@/lib/iot/gateway-cards";
import { gatewayHref } from "@/components/connect/links";
import { driverLabel, isModbusDriver, linkAddress, linkAdvancedFields, linkPrefill, preselectDriver } from "@/lib/iot/drivers";
import { hasBirth, readBirth, speaksConfigV2 } from "@/lib/iot/gateway-birth";
import { formDefaults, formToParams, validateParams, type FormValues } from "@/lib/iot/schema-form";
import { needsV1Body } from "@/lib/iot/instrument-body";
import { RegisterBrowser } from "@/components/connect/RegisterBrowser";
import { PointPicker } from "@/components/connect/PointPicker";
import { type PickedPoint } from "@/lib/iot/points";
import { mappedRegisters } from "@/lib/iot/register-map";
import { MappingTable } from "@/components/connect/MappingTable";
import { EquipmentAssign } from "@/components/connect/EquipmentAssign";
import { GoLivePanel } from "@/components/connect/GoLivePanel";

export function Connect() {
  const toast = useToast();
  const qc = useQueryClient();
  const { can } = useOrg();
  const canWrite = can("gateway.commission") || can("model.write");

  const [search] = useSearchParams();
  // "Connect a device to this gateway" (gateway detail) opens the wizard with ?gateway=<id>.
  const gatewayParam = search.get("gateway");
  const [step, setStep] = useState<ConnectStepId>("equipment");
  const [gatewayIdSel, setGatewayId] = useState<string | null>(gatewayParam);
  const [confirmReset, setConfirmReset] = useState(false);
  const [device, setDevice] = useState<DiscoveredDevice | null>(null);
  // The protocol the device is read over (the Protocol step): pre-selected from
  // the discover hit, overridable among the drivers this gateway may use.
  const [driverId, setDriverId] = useState<string | null>(null);
  // The connection form's values (null = the chosen driver's defaults + what the scan found).
  const [linkValues, setLinkValues] = useState<FormValues | null>(null);
  const [registers, setRegisters] = useState<SniffedRegister[]>([]);
  const [selectedRegs, setSelectedRegs] = useState<Set<string>>(new Set());
  // Points picked on a non-Modbus device (node tree, tag list, block scan, manual).
  const [points, setPoints] = useState<PickedPoint[]>([]);
  const [mappedRows, setMappedRows] = useState<TagRow[]>([]);
  const [entityId, setEntityId] = useState<string | null>(null);
  const [binds, setBinds] = useState<Record<string, string>>({});
  const [result, setResult] = useState<CommitResult | null>(null);
  const [busy, setBusy] = useState(false);
  // Points the server refused on the last go-live, per Map row (cleared on the next try).
  const [refused, setRefused] = useState<RefusedPoint[]>([]);

  // Gateways (the PC agents) + equipment, from the live queries. Each gateway is
  // a card (status, product, firmware); the only online multi-protocol one is
  // pre-selected.
  const thingsQuery = useThingsQuery();
  const gatewayRows = useMemo(
    () => (thingsQuery.data?.items ?? []).filter((t) => t.kind === "gateway"),
    [thingsQuery.data],
  );
  const { cards: gatewayCards } = useGatewayCards(gatewayRows);
  const gatewayPick = preferredGateway(gatewayCards);
  // The chosen gateway if still valid, else the preferred one.
  const effectiveGatewayId =
    gatewayIdSel && gatewayCards.some((g) => g.id === gatewayIdSel) ? gatewayIdSel : gatewayPick.id;
  const gatewayName = gatewayCards.find((g) => g.id === effectiveGatewayId)?.name ?? "no gateway";

  const driversQuery = useGatewayDrivers(effectiveGatewayId);
  const drivers = driversQuery.data?.drivers;
  const effectiveDriverId = driverId ?? device?.driver ?? null;
  const chosenDriver = drivers?.find((d) => d.id === effectiveDriverId);
  const gatewayThing = useGatewayThing(effectiveGatewayId);
  const birth = useMemo(() => readBirth(gatewayThing.data), [gatewayThing.data]);

  // The device's link: the connection form (from the driver's link_schema) when
  // this server lists drivers; else what the scan found (Modbus TCP, as before).
  const linkSchema = chosenDriver?.link_schema;
  const prefill = useMemo(
    () => (device && effectiveDriverId ? linkPrefill(effectiveDriverId, device) : {}),
    [device, effectiveDriverId],
  );
  const effectiveLinkValues = useMemo(
    () => linkValues ?? formDefaults(linkSchema, prefill),
    [linkValues, linkSchema, prefill],
  );
  const linkParams = useMemo(
    () => (linkSchema ? formToParams(linkSchema, effectiveLinkValues) : prefill),
    [linkSchema, effectiveLinkValues, prefill],
  );
  const linkCheck = useMemo(() => validateParams(linkSchema, linkParams), [linkSchema, linkParams]);
  // Modbus keeps the register table (browse on a v2 gateway, sniff on an older one);
  // every other driver gets the point picker for its browse kind.
  const modbus = !effectiveDriverId || isModbusDriver(effectiveDriverId);
  const canBrowse = speaksConfigV2(birth);

  const entitiesQuery = useTwinEntitiesQuery();
  const entities = useMemo(
    () => (entitiesQuery.data?.items ?? []).map(toPlantNode),
    [entitiesQuery.data],
  );
  const entityName = entities.find((e) => e.id === entityId)?.instanceName ?? "";

  const idx = CONNECT_STEP_ORDER.indexOf(step);
  const stepDef = CONNECT_STEPS[idx];

  // A new step starts at its top (WizardLayout), with focus on its region (so a
  // keyboard or screen-reader user hears where they are).
  const stepRef = useRef<HTMLElement>(null);
  const firstStep = useRef(true);
  useEffect(() => {
    if (firstStep.current) {
      firstStep.current = false;
      return;
    }
    stepRef.current?.focus({ preventScroll: true });
  }, [step]);
  const jumpTo = (s: ConnectStepId) => {
    if (canJumpTo(s, step)) setStep(s);
  };

  const pickDevice = (d: DiscoveredDevice) => {
    if (
      d.ip !== device?.ip ||
      d.unit_id !== device.unit_id ||
      d.port !== device.port ||
      d.driver !== device.driver
    ) {
      // A different device (or unit / port on it) invalidates everything downstream.
      setRegisters([]);
      setSelectedRegs(new Set());
      setPoints([]);
      setMappedRows([]);
      setBinds({});
      setResult(null);
      setDriverId(preselectDriver(d.driver, drivers));
      setLinkValues(null);
    }
    setDevice(d);
  };

  // A different protocol reads different points: the picked ones no longer apply.
  const changeDriver = (id: string) => {
    if (id === effectiveDriverId) return;
    setDriverId(id);
    setLinkValues(null);
    setRegisters([]);
    setSelectedRegs(new Set());
    setPoints([]);
    setMappedRows([]);
    setBinds({});
    setResult(null);
  };

  // Row editing (Map step) — parent owns mappedRows.
  const addRow = () => setMappedRows((rs) => [...rs, blankRow(CONNECT_POLL_DEFAULT)]);
  const removeRow = (key: string) =>
    setMappedRows((rs) => (rs.length > 1 ? rs.filter((r) => r.key !== key) : rs));
  const patchRow = (key: string, patch: Partial<TagRow>) =>
    setMappedRows((rs) => rs.map((r) => (r.key === key ? { ...r, ...patch } : r)));

  // Points -> Map: seed the tag rows from the ticked registers (Modbus) or the
  // picked points (every other driver).
  const seedRowsFromRegisters = () => {
    if (!modbus) {
      setMappedRows(points.map((p, i) => pointToTagRow(p, i, CONNECT_POLL_DEFAULT)));
      return;
    }
    // Ticked rows, less any read as part of a wider value on an earlier row.
    const chosen = mappedRegisters(registers, selectedRegs);
    setMappedRows(chosen.map((reg, i) => registerToTagRow(reg, i, CONNECT_POLL_DEFAULT)));
  };

  // Map: import a mock/plant register_map.json to seed the rows for the discovered unit —
  // each register keeps its real metric name (the operating_modes tag_hint), register space
  // (a coil onboards as boolean_state) and scale, so coils + triggers bind to the model and
  // scaled values round-trip. Ends the hand-naming of hr_N.
  const loadRegisterMap = (map: RegisterMap) => {
    if (!device) return;
    const rows = registerMapToRows(map, device.unit_id);
    if (rows.length === 0) {
      toast.push("info", `No registers for Modbus unit ${device.unit_id} in that map.`);
      return;
    }
    setMappedRows(rows);
    toast.push("ok", `Loaded ${rows.length} registers (unit ${device.unit_id}) from the map.`);
  };

  const namedCount = mappedRows.filter((r) => r.name.trim()).length;
  const nextDisabled =
    step === "equipment"
      ? !entityId
      : step === "discover"
        ? !device
        : step === "protocol"
          ? !effectiveDriverId ||
            (!!drivers && drivers.length > 0 && !chosenDriver?.available) ||
            !linkCheck.valid
          : step === "registers"
          ? modbus
            ? mappedRegisters(registers, selectedRegs).length === 0
            : points.length === 0
          : step === "map"
            ? namedCount === 0
            : step === "assign"
              ? !entityId
              : true;

  const next = () => {
    if (step === "registers") {
      seedRowsFromRegisters();
      setStep("map");
      return;
    }
    const nxt = CONNECT_STEP_ORDER[idx + 1];
    if (nxt) setStep(nxt);
  };
  const back = () => {
    const prev = CONNECT_STEP_ORDER[idx - 1];
    if (prev) setStep(prev);
  };

  const hasProgress = !!entityId || !!device || mappedRows.length > 0 || !!result;
  const reset = () => {
    setConfirmReset(false);
    setStep("equipment");
    setEntityId(null);
    setDevice(null);
    setDriverId(null);
    setLinkValues(null);
    setRegisters([]);
    setSelectedRegs(new Set());
    setPoints([]);
    setMappedRows([]);
    setBinds({});
    setResult(null);
    setRefused([]);
  };

  const summaryItems = connectSummary(
    {
      equipment: entityName,
      gateway: effectiveGatewayId ? gatewayName : null,
      device: device ? (device.name ?? device.ip) : null,
      protocol: device ? driverLabel(effectiveDriverId ?? device.driver, drivers) : null,
      points: modbus ? mappedRegisters(registers, selectedRegs).length : points.length,
    },
    step,
  );

  // Enter in a plain field advances (Discover's scan form keeps Enter = scan);
  // Ctrl/Cmd+Enter advances from anywhere in the step. A native listener on the
  // step region, read through a ref so it always sees the current step.
  const onStepKey = useRef<(e: globalThis.KeyboardEvent) => void>(() => {});
  const handleStepKey = (e: globalThis.KeyboardEvent) => {
    if (e.key !== "Enter" || e.defaultPrevented || step === "golive" || nextDisabled) return;
    if (e.ctrlKey || e.metaKey || (e.target instanceof HTMLElement && enterAdvances(e.target as HTMLInputElement))) {
      e.preventDefault();
      next();
    }
  };
  useEffect(() => {
    onStepKey.current = handleStepKey;
  });
  useEffect(() => {
    const el = stepRef.current;
    if (!el) return;
    const h = (e: globalThis.KeyboardEvent) => onStepKey.current(e);
    el.addEventListener("keydown", h);
    return () => el.removeEventListener("keydown", h);
  }, []);

  const goLive = async () => {
    if (!entityId || !device) return;
    const gw = effectiveGatewayId;
    setBusy(true);
    setRefused([]);
    try {
      if (isLiveApi && gw) {
        // Cloud is the single source of truth: createInstrument writes the tag +
        // triggers the agent's config_push over the broker (which hot-reloads its
        // poller via multiReloader), then we bind the tags to the equipment ports.
        const driver = effectiveDriverId ?? device.driver;
        const res = await commitConnection({
          gatewayId: gw,
          device,
          rows: mappedRows,
          entityId,
          binds,
          driver,
          link: linkParams,
          v1: needsV1Body(driver, { gatewaySpeaksV2: canBrowse, driversListed: (drivers?.length ?? 0) > 0 }),
        });
        setResult(res);
        void qc.invalidateQueries({ queryKey: TWIN_ENTITIES_KEY });
        if (res.bound < res.planned) {
          // Some ports kept their previous source: say so rather than toast success.
          const missed = res.planned - res.bound;
          toast.push(
            "error",
            `The device is live, but ${missed} of ${res.planned} port${res.planned === 1 ? "" : "s"} could not be bound, so ${missed === 1 ? "it keeps" : "they keep"} the previous source. Bind ${missed === 1 ? "it" : "them"} from the equipment page.`,
          );
        } else {
          toast.push("ok", goLiveToast(entityName, res));
        }
      } else {
        // Stub / offline: synthesise a result so the verify surface is explorable.
        const planned = Object.keys(binds).length;
        setResult({
          thingId: "sim-device",
          slug: slugish(device.name ?? device.ip),
          configPushed: false,
          bound: planned,
          planned,
          simulated: true,
        });
        toast.push("info", "Simulated go-live (offline demo).");
      }
    } catch (e) {
      setRefused(goLiveRowErrors(e, effectiveDriverId ?? device.driver, mappedRows));
      toast.push("error", goLiveErrorMessage(e, effectiveDriverId ?? device.driver, mappedRows));
    } finally {
      setBusy(false);
    }
  };

  return (
    <WizardLayout
      stepKey={step}
      footer={
        // Pinned to the bottom of the window, and one fixed-height row that never wraps:
        // Back at the left, Next at a fixed width at the right (an invisible stand-in on
        // the last step), the choices so far on one line between them. However many
        // choices there are, and whatever the window's width, nothing in the bar moves.
        <div className="mx-auto flex h-16 w-full max-w-6xl flex-nowrap items-center gap-3 px-4 md:px-8">
          <Button variant="ghost" onClick={back} disabled={idx === 0} className="shrink-0">
            <ArrowLeft />
            Back
          </Button>
          <div className="hidden min-w-0 flex-1 sm:block">
            <ConnectSummaryBar items={summaryItems} onStep={jumpTo} />
          </div>
          <div className="flex-1 sm:hidden" aria-hidden />
          {step !== "golive" ? (
            <Button
              onClick={next}
              disabled={nextDisabled}
              title={nextDisabled ? stepDef?.hint : undefined}
              className="w-44 shrink-0 justify-center"
            >
              <span className="truncate">Next{CONNECT_STEPS[idx + 1] ? `: ${CONNECT_STEPS[idx + 1].label}` : ""}</span>
              <ArrowRight />
            </Button>
          ) : (
            <span aria-hidden className="w-44 shrink-0" />
          )}
        </div>
      }
    >
      <PageContainer>
        <PageHeader
          title="Connect a device"
          description="Find a PLC through a gateway, pick what to read, and feed your equipment. Nothing is saved until Go live."
          breadcrumb={
            <>
              <Link to="/app" className="hover:text-on-surface">
                App
              </Link>
              <span aria-hidden>/</span>
              <Link to="/app/gateways" className="hover:text-on-surface">
                Gateways
              </Link>
              {gatewayParam && effectiveGatewayId === gatewayParam && (
                <>
                  <span aria-hidden>/</span>
                  <Link to={gatewayHref(gatewayParam)} className="hover:text-on-surface">
                    {gatewayName}
                  </Link>
                </>
              )}
              <span aria-hidden>/</span>
              <span className="text-on-surface">Connect a device</span>
              <span aria-hidden>/</span>
              <span className="text-on-surface-muted" aria-current="step">
                {stepDef?.label}
              </span>
            </>
          }
          actions={
            hasProgress ? (
              <Button variant="ghost" size="sm" onClick={() => (result ? reset() : setConfirmReset(true))}>
                Start over
              </Button>
            ) : undefined
          }
        />

        <ConnectStepper current={step} onStep={jumpTo} />

        <section
          ref={stepRef}
          tabIndex={-1}
          aria-label={`Step ${idx + 1} of ${CONNECT_STEPS.length}: ${stepDef?.label ?? ""}`}
          className="focus:outline-none"
        >
          {step === "equipment" && (
            <EquipmentAssign
              variant="pick"
              entities={entities}
              entitiesLoading={entitiesQuery.isLoading}
              entityId={entityId}
              onEntityId={setEntityId}
              rows={mappedRows}
              binds={binds}
              onBinds={setBinds}
            />
          )}

          {step === "discover" && (
            <DeviceScanPanel
              cards={gatewayCards}
              gatewaysLoading={thingsQuery.isLoading}
              gatewayId={effectiveGatewayId}
              gatewayObvious={
                (!!gatewayParam && effectiveGatewayId === gatewayParam) ||
                (gatewayPick.obvious && effectiveGatewayId === gatewayPick.id)
              }
              onGatewayId={setGatewayId}
              selected={device}
              onPick={pickDevice}
              onUse={(d) => {
                pickDevice(d);
                setStep("protocol");
              }}
            />
          )}

          {step === "protocol" &&
            (device && effectiveDriverId ? (
              <ProtocolStep
                device={device}
                drivers={drivers}
                driversLoading={driversQuery.isLoading}
                driversUnavailable={driversQuery.isError || !isLiveApi}
                driverId={effectiveDriverId}
                onDriverId={changeDriver}
              >
                {linkSchema && chosenDriver?.available && (
                  <ConnectionForm
                    driverLabel={driverLabel(effectiveDriverId, drivers)}
                    schema={linkSchema}
                    values={effectiveLinkValues}
                    onChange={setLinkValues}
                    errors={linkCheck.errors}
                    serialPorts={hasBirth(gatewayThing.data) ? birth.serialPorts : undefined}
                    prefilled={device.online}
                    advanced={linkAdvancedFields(effectiveDriverId)}
                  />
                )}
              </ProtocolStep>
            ) : (
              <NoDeviceYet />
            ))}

          {step === "registers" &&
            (device && !modbus && effectiveDriverId ? (
              <PointPicker
                gatewayId={effectiveGatewayId}
                gatewayName={gatewayName}
                driver={chosenDriver}
                driverId={effectiveDriverId}
                link={linkParams}
                deviceLabel={`${driverLabel(effectiveDriverId ?? device.driver, drivers)} ${linkAddress(linkParams, device)}`}
                deviceProduct={device.identity?.product}
                picked={points}
                onPicked={setPoints}
                onContinue={next}
              />
            ) : device ? (
              <RegisterBrowser
                device={device}
                driverId={effectiveDriverId ?? device.driver}
                link={linkParams}
                canBrowse={canBrowse}
                gatewayId={effectiveGatewayId}
                gatewayName={gatewayName}
                registers={registers}
                onRegisters={setRegisters}
                selectedIds={selectedRegs}
                onSelectionChange={setSelectedRegs}
                onContinue={next}
              />
            ) : (
              <NoDeviceYet />
            ))}

          {step === "map" &&
            (device ? (
              <MappingTable
                rows={mappedRows}
                onAddRow={addRow}
                onRemoveRow={removeRow}
                onPatchRow={patchRow}
                onLoadRegisterMap={modbus ? loadRegisterMap : undefined}
                pointDriver={modbus ? undefined : chosenDriver}
              />
            ) : (
              <NoDeviceYet />
            ))}

          {step === "assign" &&
            (device ? (
              <EquipmentAssign
                entities={entities}
                entitiesLoading={entitiesQuery.isLoading}
                entityId={entityId}
                onEntityId={setEntityId}
                rows={mappedRows}
                binds={binds}
                onBinds={setBinds}
              />
            ) : (
              <NoDeviceYet />
            ))}

          {step === "golive" &&
            (device ? (
              <GoLivePanel
                device={device}
                protocolLabel={driverLabel(effectiveDriverId ?? device.driver, drivers)}
                gatewayName={gatewayName}
                gatewayId={effectiveGatewayId}
                entityId={entityId}
                entityName={entityName}
                rows={mappedRows}
                binds={binds}
                result={result}
                busy={busy}
                refused={refused}
                canWrite={canWrite}
                onGoLive={goLive}
                onConnectAnother={reset}
              />
            ) : (
              <NoDeviceYet />
            ))}
        </section>

        <ConfirmDialog
          open={confirmReset}
          onClose={() => setConfirmReset(false)}
          onConfirm={reset}
          title="Start over?"
          description="This clears the equipment, device and points you picked. Nothing has been saved yet."
          confirmLabel="Start over"
          destructive
        />
      </PageContainer>
    </WizardLayout>
  );
}

function NoDeviceYet() {
  return (
    <div className="rounded-lg border border-dashed border-border bg-surface-elevated/40 p-8 text-center text-sm text-on-surface-muted">
      Pick a device on the Discover step first.
    </div>
  );
}
